import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip';
import { MessageSquare } from 'lucide-react';

/** The states a `CommentSummary.state` can report, on any subject that exposes the field. */
const COMMENTS_INDICATOR_STATES = ['none', 'inactive', 'active'] as const;

export type CommentsIndicatorState = (typeof COMMENTS_INDICATOR_STATES)[number];

/**
 * The schema types `state` as String, so this narrows without an assertion. An unrecognized
 * value fails closed to the quietest rendering rather than throwing inside a table cell.
 */
export function toCommentsIndicatorState(value: string): CommentsIndicatorState {
  return COMMENTS_INDICATOR_STATES.find((state) => state === value) ?? 'none';
}

type CommentsIndicatorProps = {
  readonly state: CommentsIndicatorState;
  readonly totalCount: number;
  readonly unreadCount: number;
};

function pluralizedCountLabel(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function commentsIndicatorLabel(state: CommentsIndicatorState, totalCount: number, unreadCount: number): string {
  if (state === 'none') {
    return 'No comments';
  }
  if (state === 'inactive') {
    return 'No unread comments';
  }
  return unreadCount > 0
    ? pluralizedCountLabel(unreadCount, 'unread comment', 'unread comments')
    : pluralizedCountLabel(totalCount, 'comment', 'comments');
}

/**
 * Three states, not the task column's four: this glyph is watermark-driven, so it clears when
 * the viewer opens the thread, where the task column's `active_mention` persists until the
 * alert is dismissed. Switched on the server's `state` rather than re-deriving it from the
 * counts, so there is one definition of the rule.
 *
 * `role="img"` is load-bearing: ARIA forbids naming the implicit `generic` role, so an
 * aria-label on a bare span is unreliable across screen readers — and Testing Library's
 * getByLabelText matches the attribute rather than the computed name, so a test would pass
 * either way.
 */
export function CommentsIndicator({ state, totalCount, unreadCount }: CommentsIndicatorProps) {
  const label = commentsIndicatorLabel(state, totalCount, unreadCount);

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          state === 'none' ? (
            <span className="text-secondary" role="img" aria-label={label}>
              —
            </span>
          ) : (
            <span className="relative inline-flex items-center" role="img" aria-label={label}>
              <MessageSquare className="size-4 text-secondary" aria-hidden="true" />
              {state === 'active' && (
                // ring-2 draws outside the box, so the dot keeps its full 6px and gains a 2px
                // gap; a border would eat into the 6px and read noticeably smaller. The gap is
                // repainted on row hover so it tracks the row rather than flashing card white.
                <span
                  className="absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-savvy-green ring-2 ring-card [tr:hover_&]:ring-table-hover"
                  aria-hidden="true"
                />
              )}
            </span>
          )
        }
      />
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  );
}
